/**
 * Helpers for reporting what went wrong when applying violations in bulk.
 *
 * Applying used to fire a blocking alert per failed violation and then claim
 * success regardless, so a batch of 20 meant clicking through 20 dialogs to be
 * told everything worked when nothing had. These build one honest summary.
 */

const MAX_LISTED_FAILURES = 10

/**
 * Turn whatever the API gave us into a sentence a reviewer can act on.
 *
 * The API's error handler returns { status, code, message } and the message is
 * written for the caller, so prefer it. Fall back through the thrown error and
 * finally to a generic line — never show an empty reason.
 */
export function describeApplyFailure(response, error) {
	if (response && typeof response.message === 'string' && response.message) {
		return response.message
	}
	if (response && typeof response.code === 'string' && response.code) {
		return response.code
	}
	if (error && error.message) {
		return error.message
	}
	return 'The server rejected the action without saying why.'
}

/**
 * A human-recognisable label for a violation: what the reviewer sees on the card,
 * not the UUID. Falls back to the id when the violation is no longer in the list.
 */
export function describeViolation(violations, violationId) {
	const violation = (violations || []).find((v) => v.violation_id === violationId)
	if (!violation) return violationId

	const parts = []
	if (violation.domain) parts.push(violation.domain)
	if (violation.name) parts.push(`image ${violation.name}`)
	if (violation.case_id) parts.push(violation.case_id)

	return parts.length > 0 ? `${parts.join(' · ')} (${violationId})` : violationId
}

/**
 * One summary for the whole batch: how many applied, how many did not, and why.
 * Identical reasons are grouped so 20 copies of the same problem read as one line.
 */
export function summariseApplyFailures(appliedCount, totalCount, failures) {
	const byMessage = new Map()
	failures.forEach(({ label, violationId, message }) => {
		if (!byMessage.has(message)) byMessage.set(message, [])
		byMessage.get(message).push(label || violationId)
	})

	const sections = []
	byMessage.forEach((labels, message) => {
		const listed = labels.slice(0, MAX_LISTED_FAILURES).map((l) => `   • ${l}`)
		const hidden = labels.length - listed.length
		if (hidden > 0) listed.push(`   • …and ${hidden} more`)
		sections.push(`${message}\n${listed.join('\n')}`)
	})

	return (
		`Applied ${appliedCount} of ${totalCount} violation(s).\n\n` +
		`${failures.length} could not be applied and are still selected so you can retry:\n\n` +
		sections.join('\n\n')
	)
}
